import { motion, AnimatePresence } from "motion/react";
import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { createPortal } from "react-dom";
import { useSound } from "@/contexts/SoundContext";
import { useRouter } from "next/navigation";

const RARITY_COLORS = {
    common: {
        primary: "#94a3b8",
        secondary: "#cbd5e1",
        dark: "#475569",
    },
    uncommon: {
        primary: "#10b981",
        secondary: "#34d399",
        dark: "#059669",
    },
    rare: {
        primary: "#3b82f6",
        secondary: "#60a5fa",
        dark: "#1d4ed8",
    },
    epic: {
        primary: "#a855f7",
        secondary: "#c084fc",
        dark: "#6d28d9",
    },
    legendary: {
        primary: "#f59e0b",
        secondary: "#fbbf24",
        dark: "#d97706",
    },
    gold: {
        primary: "#f59e0b",
        secondary: "#fbbf24",
        dark: "#d97706",
    },
};

function getChestImage(rarity, chestType) {
    if (chestType === "gold") return "/static/icons/chests/legendary.svg";
    switch (rarity) {
        case "legendary":
            return "/static/icons/chests/legendary.svg";
        case "epic":
            return "/static/icons/chests/epic.svg";
        case "rare":
            return "/static/icons/chests/rare.svg";
        case "uncommon":
            return "/static/icons/chests/uncommon.svg";
        default:
            return "/static/icons/chests/common.svg";
    }
}

const getRewardIcon = (type) => {
    switch (type) {
        case "xp":
        case "xp_bonus":
            return "/static/icons/xp.svg";
        case "fragment":
            return "/static/icons/fragment.svg";
        case "shield":
            return "/static/icons/shield.svg";
        case "xp_booster":
            return "/static/icons/boost.svg";
        case "bonus_key":
            return "/static/icons/key.svg";
        default:
            return "/static/icons/gift.svg";
    }
};

// Son joué à la révélation de CHAQUE récompense, selon son type. Fichiers dédiés à
// déposer dans public/static/sounds/ (clé = bruit de clé, bouclier = son sourd,
// fragment = pluie de pièces...). Tant qu'un fichier manque, playSound échoue
// silencieusement (console.warn) sans casser l'animation — le défaut sert de filet.
const REWARD_SOUNDS = {
    bonus_key: { file: "/static/sounds/reward-key.mp3", volume: 0.34 },
    shield: { file: "/static/sounds/reward-shield.mp3", volume: 0.34 },
    fragment: { file: "/static/sounds/reward-fragment.mp3", volume: 0.34 },
    xp: { file: "/static/sounds/reward-xp.mp3", volume: 0.28 },
    xp_bonus: { file: "/static/sounds/reward-xp.mp3", volume: 0.28 },
    xp_booster: { file: "/static/sounds/reward-boost.mp3", volume: 0.28 },
};
const DEFAULT_REWARD_SOUND = {
    file: "/static/sounds/chest-upgrade.mp3",
    volume: 0.2,
};

const RARITY_ORDER = ["common", "uncommon", "rare", "epic", "legendary"];

function generateTransformationSequence(finalRarity) {
    const finalIndex = RARITY_ORDER.indexOf(finalRarity);
    if (finalIndex === -1 || finalIndex === 0) return Array(5).fill("common");
    if (finalRarity === "legendary") {
        return ["common", "uncommon", "rare", "epic", "legendary"];
    }

    const sequence = [];
    let currentRarityIndex = 0;
    for (let i = 0; i < 5; i++) {
        if (i === 4) {
            sequence.push(finalRarity);
        } else {
            const remainingSteps = finalIndex - currentRarityIndex;
            const remainingTaps = 4 - i;
            const mustProgress = remainingSteps >= remainingTaps;
            const shouldProgress =
                mustProgress ||
                (currentRarityIndex < finalIndex && Math.random() < 0.4);
            if (shouldProgress && currentRarityIndex < finalIndex) {
                currentRarityIndex++;
            }
            sequence.push(RARITY_ORDER[currentRarityIndex]);
        }
    }
    return sequence;
}

export default function ChestOpeningModalV4({
    isOpen,
    onClose,
    rewards,
    chestType = "normal",
    // Coffre tutoriel : une phrase par type de récompense ({ shield: "..." }),
    // affichée sous son nom au moment où elle est révélée.
    rewardHints = null,
}) {
    const t = useTranslations();
    const { playSound } = useSound();
    const router = useRouter();

    const translateRewardType = (type, amount = 0, metadata = null) => {
        const translationKey = `reward_type_${type}`;
        if (t.has && t.has(translationKey)) {
            return t(translationKey, {
                amount,
                multiplier: metadata?.multiplier ?? 0,
                minutes: metadata?.durationMinutes ?? 0,
            });
        }
        return t.has && t.has("reward_type_bonus")
            ? t("reward_type_bonus")
            : "Bonus";
    };

    const [tapCount, setTapCount] = useState(0);
    const [phase, setPhase] = useState("idle");
    const [mounted, setMounted] = useState(false);
    const [shouldWiggle, setShouldWiggle] = useState(false);
    const [currentDisplayRarity, setCurrentDisplayRarity] = useState("common");
    const [currentChestIndex, setCurrentChestIndex] = useState(0);
    // Révélation une par une : index de la récompense en cours d'affichage.
    // Quand revealStep dépasse le nombre de récompenses, on passe au récap.
    const [revealStep, setRevealStep] = useState(0);
    const [transformationSequence, setTransformationSequence] = useState(() => {
        const firstDropRarity =
            rewards?.drops?.[0]?.rarity || rewards?.rarity || "common";
        return generateTransformationSequence(firstDropRarity);
    });

    const drops = rewards?.drops || [];
    const isDoubleChest = rewards?.event === "double_chest" && drops.length > 1;
    const currentDrop = drops[currentChestIndex] || drops[0] || {};
    const rarity = isDoubleChest
        ? currentDrop?.rarity || "common"
        : currentDrop?.rarity || rewards?.rarity || "common";
    const normalRewards = currentDrop?.rewards || [];
    const xpGained =
        currentChestIndex === 0 ? rewards?.xp || rewards?.xpGained || 0 : 0;

    // Liste des récompenses à révéler une par une : l'XP de base d'abord (si > 0),
    // puis chaque récompense du coffre. Le récap final les réaffiche toutes.
    const revealItems = [
        ...(xpGained > 0 ? [{ type: "xp", amount: xpGained, _xp: true }] : []),
        ...normalRewards,
    ];
    const inRecap = revealStep >= revealItems.length;
    const revealRemaining = revealItems.length - 1 - revealStep;
    const revealIsLast = revealStep === revealItems.length - 1;
    const currentRevealItem = revealItems[revealStep] || null;
    const revealIcon = currentRevealItem?._xp
        ? "/static/icons/xp.svg"
        : currentRevealItem
          ? getRewardIcon(currentRevealItem.type)
          : "";
    const revealAmountText = currentRevealItem?._xp
        ? `+${currentRevealItem.amount}`
        : currentRevealItem?.type === "xp_booster"
          ? `×${currentRevealItem?.metadata?.multiplier ?? 2}`
          : `+${currentRevealItem?.amount ?? 0}`;

    const multipliers = rewards?.multipliers || null;
    const hasActiveBoost =
        multipliers &&
        (multipliers.xp > 1 ||
            multipliers.fragment > 1 ||
            multipliers.shield > 1);
    const isPremiumEvent = rewards?.event?.endsWith("_premium");
    const isPremiumBoostEvent =
        isPremiumEvent && !rewards?.event?.includes("double_chest");

    useEffect(() => {
        setMounted(true);
    }, []);

    useEffect(() => {
        if (isOpen) {
            setTapCount(0);
            setPhase("idle");
            setShouldWiggle(false);
            setCurrentDisplayRarity("common");
            setCurrentChestIndex(0);
            setRevealStep(0);

            const firstDropRarity =
                rewards?.drops?.[0]?.rarity || rewards?.rarity || "common";
            setTransformationSequence(
                generateTransformationSequence(firstDropRarity),
            );

            playSound("/static/sounds/chest-appear.mp3", { volume: 0.21 });

            const wiggleTimer = setTimeout(() => setShouldWiggle(true), 1500);
            return () => clearTimeout(wiggleTimer);
        }
    }, [isOpen, rewards]);

    // Un booster tombé d'un coffre est déjà en base au moment où la modale
    // s'ouvre (la réclamation le crée avant de renvoyer le butin). On prévient
    // donc la barre du haut tout de suite : quand la modale se referme, la
    // pastille est déjà là, et la question "il est parti où ?" ne se pose pas.
    useEffect(() => {
        if (!isOpen) return;
        const drops = rewards?.drops || [];
        const gotBooster = drops.some((drop) =>
            (drop?.rewards || []).some((r) => r?.type === "xp_booster"),
        );
        if (gotBooster) {
            window.dispatchEvent(new Event("reps:boosters-changed"));
        }
    }, [isOpen, rewards]);

    useEffect(() => {
        if (tapCount > 0 && phase === "idle") {
            setShouldWiggle(false);
            const inactivityTimer = setTimeout(
                () => setShouldWiggle(true),
                3000,
            );
            return () => clearTimeout(inactivityTimer);
        }
    }, [tapCount, phase]);

    if (!mounted) return null;

    // Joue le son correspondant au type de récompense révélée.
    const playRewardSound = (item) => {
        const type = item?._xp ? "xp" : item?.type;
        const sound = REWARD_SOUNDS[type] || DEFAULT_REWARD_SOUND;
        playSound(sound.file, { volume: sound.volume });
    };

    const handleTap = () => {
        if (phase !== "idle") return;

        setShouldWiggle(false);
        const newTapCount = tapCount + 1;
        setTapCount(newTapCount);

        let rarityChanged = false;
        if (newTapCount <= 5) {
            const newRarity =
                transformationSequence[newTapCount - 1] || "common";
            if (newRarity !== currentDisplayRarity) {
                setCurrentDisplayRarity(newRarity);
                rarityChanged = true;
            }
        }

        let soundFile;
        let volume;
        if (newTapCount >= 6) {
            soundFile = "/static/sounds/chest-opening.mp3";
            volume = 0.3;
        } else if (rarityChanged) {
            soundFile = "/static/sounds/chest-upgrade.mp3";
            volume = 0.21;
        } else {
            soundFile = `/static/sounds/tap-${newTapCount}.mp3`;
            volume = 0.17;
        }
        playSound(soundFile, { volume });

        if (newTapCount >= 6) {
            setPhase("shaking");
            setTimeout(() => setPhase("flashing"), 280);
            setTimeout(() => {
                setPhase("revealing");
                // Son de la 1re récompense (les suivantes via advanceReveal).
                if (revealItems[0]) playRewardSound(revealItems[0]);
            }, 530);
        }
    };

    const goToNextChest = () => {
        setPhase("transition");
        setTimeout(() => {
            setCurrentChestIndex(1);
            setTapCount(0);
            setRevealStep(0);
            setPhase("idle");
            setShouldWiggle(false);
            setCurrentDisplayRarity("common");
            const secondRarity = drops[1]?.rarity || "common";
            setTransformationSequence(
                generateTransformationSequence(secondRarity),
            );
            playSound("/static/sounds/chest-appear.mp3", { volume: 0.21 });
            setTimeout(() => setShouldWiggle(true), 1500);
        }, 220);
    };

    // Avance d'une récompense dans la révélation une par une (puis le récap).
    const advanceReveal = () => {
        const next = revealItems[revealStep + 1];
        if (next) {
            playRewardSound(next);
        } else {
            // Passage au récap.
            playSound("/static/sounds/pop.mp3", { volume: 0.2 });
        }
        setRevealStep((s) => s + 1);
    };

    const colors = RARITY_COLORS[currentDisplayRarity] || RARITY_COLORS.common;
    const rewardColors = RARITY_COLORS[rarity] || RARITY_COLORS.common;
    const showChest = phase === "idle" || phase === "shaking";
    const showRewards = phase === "revealing";
    const showFlash = phase === "flashing" || phase === "shaking";

    const modalContent = (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/70 backdrop-blur-md overflow-hidden"
                >
                    {/* Halo radial central — présent dès l'ouverture, intensifie selon la phase */}
                    <motion.div
                        className="absolute inset-0 pointer-events-none"
                        animate={{
                            opacity: showRewards ? 1 : tapCount > 0 ? 0.7 : 0.3,
                        }}
                        transition={{ duration: 0.5, ease: "easeOut" }}
                    >
                        <div
                            className="absolute inset-0"
                            style={{
                                background: `radial-gradient(circle at 50% 50%, ${
                                    showRewards
                                        ? rewardColors.primary
                                        : colors.primary
                                }${showRewards ? "55" : "30"} 0%, transparent 55%)`,
                            }}
                        />
                    </motion.div>

                    {/* Indicateur 1/2 ou 2/2 pour double chest */}
                    {showChest && isDoubleChest && (
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3, duration: 0.3 }}
                            className="fixed top-8 left-1/2 -translate-x-1/2 z-[100000] pointer-events-none"
                        >
                            <div className="px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full border border-white/15">
                                <span className="text-white/80 font-semibold text-xs tracking-wider">
                                    {currentChestIndex + 1} / 2
                                </span>
                            </div>
                        </motion.div>
                    )}

                    <AnimatePresence mode="wait">
                        {showChest && (
                            <motion.div
                                key="chest"
                                className="relative z-10"
                                initial={{ scale: 0.7, opacity: 0 }}
                                animate={{
                                    scale: 1,
                                    opacity: 1,
                                }}
                                exit={{ opacity: 0, transition: { duration: 0.1 } }}
                                transition={{
                                    type: "spring",
                                    stiffness: 280,
                                    damping: 22,
                                }}
                            >
                                {/* Glow doux derrière le coffre, change avec la rareté */}
                                <motion.div
                                    className="absolute inset-0 -m-12 pointer-events-none"
                                    animate={{
                                        opacity: tapCount > 0 ? 1 : 0.4,
                                    }}
                                    transition={{ duration: 0.4 }}
                                >
                                    <motion.div
                                        className="w-full h-full rounded-full"
                                        style={{
                                            background: `radial-gradient(circle, ${colors.primary}55 0%, ${colors.primary}20 40%, transparent 70%)`,
                                            filter: "blur(28px)",
                                        }}
                                        animate={{
                                            scale:
                                                phase === "shaking"
                                                    ? [1, 1.4]
                                                    : [1, 1.08, 1],
                                        }}
                                        transition={
                                            phase === "shaking"
                                                ? { duration: 0.3 }
                                                : {
                                                      duration: 2.5,
                                                      repeat: Infinity,
                                                      ease: "easeInOut",
                                                  }
                                        }
                                    />
                                </motion.div>

                                {/* Coffre cliquable */}
                                <motion.button
                                    onClick={handleTap}
                                    className="relative p-8 cursor-pointer select-none"
                                    whileTap={{ scale: 0.92 }}
                                    animate={
                                        phase === "shaking"
                                            ? {
                                                  x: [
                                                      0, -8, 8, -10, 10, -6, 6,
                                                      -3, 3, 0,
                                                  ],
                                                  y: [
                                                      0, 2, -2, 3, -3, 1, -1, 0,
                                                      0, 0,
                                                  ],
                                                  rotate: [
                                                      0, -3, 3, -4, 4, -2, 2,
                                                      -1, 1, 0,
                                                  ],
                                              }
                                            : shouldWiggle
                                              ? {
                                                    x: [0, -2, 2, -1, 1, 0],
                                                    rotate: [
                                                        0, -1.5, 1.5, -0.5,
                                                        0.5, 0,
                                                    ],
                                                }
                                              : tapCount > 0
                                                ? {
                                                      rotate: [
                                                          0, -4, 4, -2, 2, 0,
                                                      ],
                                                      y: [0, -3, 0],
                                                  }
                                                : {}
                                    }
                                    transition={
                                        phase === "shaking"
                                            ? { duration: 0.28, ease: "easeInOut" }
                                            : shouldWiggle
                                              ? {
                                                    duration: 0.7,
                                                    repeat: Infinity,
                                                    repeatDelay: 2.2,
                                                    ease: "easeInOut",
                                                }
                                              : tapCount > 0
                                                ? { duration: 0.35, ease: "easeOut" }
                                                : {}
                                    }
                                >
                                    <div className="relative w-[150px] h-[150px] flex items-center justify-center">
                                        <AnimatePresence mode="wait">
                                            <motion.div
                                                key={`${currentDisplayRarity}-${currentChestIndex}`}
                                                initial={{
                                                    scale: 0.85,
                                                    opacity: 0,
                                                }}
                                                animate={{
                                                    scale: 1,
                                                    opacity: 1,
                                                    filter:
                                                        tapCount > 0
                                                            ? `brightness(${1 + tapCount * 0.04}) drop-shadow(0 0 12px ${colors.primary}aa)`
                                                            : "brightness(1)",
                                                }}
                                                exit={{
                                                    scale: 1.05,
                                                    opacity: 0,
                                                }}
                                                transition={{
                                                    type: "spring",
                                                    stiffness: 350,
                                                    damping: 28,
                                                    opacity: { duration: 0.18 },
                                                }}
                                                className="absolute inset-0 flex items-center justify-center"
                                            >
                                                <Image
                                                    src={getChestImage(
                                                        currentDisplayRarity,
                                                        chestType,
                                                    )}
                                                    alt={`Chest ${currentDisplayRarity}`}
                                                    width={150}
                                                    height={150}
                                                    className="drop-shadow-2xl"
                                                    priority
                                                    unoptimized
                                                />
                                            </motion.div>
                                        </AnimatePresence>
                                    </div>
                                </motion.button>
                            </motion.div>
                        )}

                        {showRewards && (
                            <motion.div
                                key="rewards"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.4 }}
                                className="relative z-10 w-full h-full flex items-center justify-center"
                            >
                                {!inRecap ? (
                                    <OneByOneReveal
                                        index={revealStep}
                                        total={revealItems.length}
                                        remaining={revealRemaining}
                                        colors={rewardColors}
                                        icon={revealIcon}
                                        amountText={revealAmountText}
                                        label={
                                            currentRevealItem?._xp
                                                ? t("experience")
                                                : currentRevealItem
                                                  ? translateRewardType(
                                                        currentRevealItem.type,
                                                        currentRevealItem.amount,
                                                        currentRevealItem.metadata,
                                                    )
                                                  : ""
                                        }
                                        hint={
                                            (currentRevealItem &&
                                                rewardHints?.[
                                                    currentRevealItem.type
                                                ]) ||
                                            null
                                        }
                                        isLast={revealIsLast}
                                        onAdvance={advanceReveal}
                                        t={t}
                                    />
                                ) : (
                                <div className="relative max-w-lg w-full mx-4 px-4">
                                    {/* En-tête récap : « Voici ton butin » + compte */}
                                    <motion.div
                                        initial={{ y: -16, opacity: 0 }}
                                        animate={{ y: 0, opacity: 1 }}
                                        transition={{
                                            delay: 0.05,
                                            duration: 0.4,
                                            ease: "easeOut",
                                        }}
                                        className="text-center mb-6"
                                    >
                                        <h1 className="text-2xl sm:text-3xl font-black text-white mb-1.5 tracking-tight">
                                            {t("chest_recap_title")}
                                        </h1>
                                        <p className="text-sm text-white/55 mb-0">
                                            {revealItems.length > 1
                                                ? t("chest_recap_subtitle", {
                                                      count: revealItems.length,
                                                  })
                                                : t("chest_recap_subtitle_one")}
                                        </p>
                                    </motion.div>

                                    {/* Bandeau double_chest */}
                                    {rewards?.event === "double_chest" && (
                                        <motion.div
                                            initial={{ y: -10, opacity: 0 }}
                                            animate={{ y: 0, opacity: 1 }}
                                            transition={{ delay: 0.2 }}
                                            className="text-center mb-4"
                                        >
                                            <div className="inline-block px-3 py-1.5 bg-primary/20 text-primary border border-primary/30 font-medium text-xs sm:text-sm rounded-lg">
                                                {t(
                                                    "double_chest_event_active",
                                                )}
                                            </div>
                                        </motion.div>
                                    )}

                                    {/* Bandeau premium boost (non accessible) */}
                                    {isPremiumBoostEvent && (
                                        <motion.div
                                            initial={{ y: -10, opacity: 0 }}
                                            animate={{ y: 0, opacity: 1 }}
                                            transition={{ delay: 0.2 }}
                                            className="text-left mb-4"
                                        >
                                            <div className="relative bg-gradient-to-br from-purple-900/40 via-pink-800/30 to-amber-600/20 rounded-2xl border border-purple-400/40 backdrop-blur-xl p-4 shadow-xl overflow-hidden">
                                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 animate-pulse opacity-60" />
                                                <div className="relative flex flex-col items-start gap-3">
                                                    <div className="flex items-center gap-3">
                                                        <motion.span
                                                            animate={{
                                                                rotate: [
                                                                    0, -5, 5, 0,
                                                                ],
                                                            }}
                                                            transition={{
                                                                duration: 3,
                                                                repeat: Infinity,
                                                                ease: "easeInOut",
                                                            }}
                                                            className="text-2xl"
                                                        >
                                                            👑
                                                        </motion.span>
                                                        <h3 className="text-transparent bg-clip-text bg-gradient-to-r from-purple-200 via-pink-200 to-amber-200 font-bold text-lg mb-0 tracking-wide">
                                                            {t(
                                                                "premium_event_title",
                                                            )}
                                                        </h3>
                                                    </div>
                                                    <p className="text-purple-100/90 text-sm mb-0 leading-relaxed">
                                                        {t(
                                                            "premium_event_message",
                                                        )}
                                                    </p>
                                                    <div className="flex flex-wrap gap-2">
                                                        {multipliers?.xp > 1 && (
                                                            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-blue-600/30 to-cyan-500/30 rounded-lg border border-blue-400/40">
                                                                <Image
                                                                    src="/static/icons/xp.svg"
                                                                    width={14}
                                                                    height={14}
                                                                    alt="XP"
                                                                />
                                                                <span className="text-blue-200 text-xs font-semibold">
                                                                    {t(
                                                                        "premium_event_missed_xp",
                                                                        {
                                                                            multiplier:
                                                                                multipliers.xp,
                                                                        },
                                                                    )}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {multipliers?.fragment >
                                                            1 && (
                                                            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-purple-600/30 to-pink-500/30 rounded-lg border border-purple-400/40">
                                                                <Image
                                                                    src="/static/icons/fragment.svg"
                                                                    width={14}
                                                                    height={14}
                                                                    alt="Fragment"
                                                                />
                                                                <span className="text-purple-200 text-xs font-semibold">
                                                                    {t(
                                                                        "premium_event_missed_fragment",
                                                                        {
                                                                            multiplier:
                                                                                multipliers.fragment,
                                                                        },
                                                                    )}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {multipliers?.shield >
                                                            1 && (
                                                            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-emerald-600/30 to-teal-500/30 rounded-lg border border-emerald-400/40">
                                                                <Image
                                                                    src="/static/icons/shield.svg"
                                                                    width={14}
                                                                    height={14}
                                                                    alt="Shield"
                                                                />
                                                                <span className="text-emerald-200 text-xs font-semibold">
                                                                    {t(
                                                                        "premium_event_missed_shield",
                                                                        {
                                                                            multiplier:
                                                                                multipliers.shield,
                                                                        },
                                                                    )}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <motion.button
                                                        onClick={() =>
                                                            router.push(
                                                                "/pricing",
                                                            )
                                                        }
                                                        whileHover={{
                                                            scale: 1.05,
                                                        }}
                                                        whileTap={{
                                                            scale: 0.95,
                                                        }}
                                                        className="group relative px-4 py-2 bg-gradient-to-r from-purple-600 via-pink-500 to-amber-500 text-white font-bold rounded-lg text-sm shadow-lg hover:shadow-purple-500/30 transition-all overflow-hidden self-start"
                                                    >
                                                        <span className="relative z-10 flex items-center gap-1.5">
                                                            <span>✨</span>
                                                            {t(
                                                                "premium_event_cta",
                                                            )}
                                                        </span>
                                                    </motion.button>
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}

                                    {/* Badges multiplicateurs actifs (event normal) */}
                                    {hasActiveBoost && !isPremiumEvent && (
                                        <motion.div
                                            initial={{ opacity: 0, y: -10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.15 }}
                                            className="flex flex-wrap justify-center gap-2 mb-4"
                                        >
                                            {multipliers.xp > 1 && (
                                                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-lg border border-blue-400/30 backdrop-blur-sm">
                                                    <Image
                                                        src="/static/icons/xp.svg"
                                                        width={14}
                                                        height={14}
                                                        alt="XP"
                                                    />
                                                    <span className="text-blue-200 font-bold text-sm">
                                                        {t("event_xp_boost", {
                                                            multiplier:
                                                                multipliers.xp,
                                                        })}
                                                    </span>
                                                </div>
                                            )}
                                            {multipliers.fragment > 1 && (
                                                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-lg border border-purple-400/30 backdrop-blur-sm">
                                                    <Image
                                                        src="/static/icons/fragment.svg"
                                                        width={14}
                                                        height={14}
                                                        alt="Fragment"
                                                    />
                                                    <span className="text-purple-200 font-bold text-sm">
                                                        {t(
                                                            "event_fragment_boost",
                                                            {
                                                                multiplier:
                                                                    multipliers.fragment,
                                                            },
                                                        )}
                                                    </span>
                                                </div>
                                            )}
                                            {multipliers.shield > 1 && (
                                                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-indigo-500/20 to-blue-500/20 rounded-lg border border-indigo-400/30 backdrop-blur-sm">
                                                    <Image
                                                        src="/static/icons/shield.svg"
                                                        width={14}
                                                        height={14}
                                                        alt="Shield"
                                                    />
                                                    <span className="text-indigo-200 font-bold text-sm">
                                                        {t(
                                                            "event_shield_boost",
                                                            {
                                                                multiplier:
                                                                    multipliers.shield,
                                                            },
                                                        )}
                                                    </span>
                                                </div>
                                            )}
                                        </motion.div>
                                    )}

                                    {/* Bandeau double_chest_premium */}
                                    {rewards?.event ===
                                        "double_chest_premium" && (
                                        <motion.div
                                            initial={{ y: -10, opacity: 0 }}
                                            animate={{ y: 0, opacity: 1 }}
                                            transition={{ delay: 0.2 }}
                                            className="text-center mb-4"
                                        >
                                            <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-xl border border-purple-400/30 backdrop-blur-sm p-4">
                                                <div className="flex flex-col items-center gap-3">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-2xl">
                                                            👑
                                                        </span>
                                                        <p className="text-purple-200 font-bold text-base sm:text-lg mb-0">
                                                            {t(
                                                                "double_chest_premium_title",
                                                            )}
                                                        </p>
                                                    </div>
                                                    <p className="text-purple-100 text-sm sm:text-base mb-0">
                                                        {t(
                                                            "double_chest_premium_message",
                                                        )}
                                                    </p>
                                                    <motion.button
                                                        onClick={() =>
                                                            router.push(
                                                                "/pricing",
                                                            )
                                                        }
                                                        whileHover={{
                                                            scale: 1.05,
                                                        }}
                                                        whileTap={{
                                                            scale: 0.95,
                                                        }}
                                                        className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold rounded-lg text-sm hover:shadow-lg transition-shadow"
                                                    >
                                                        {t(
                                                            "double_chest_premium_cta",
                                                        )}
                                                    </motion.button>
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}

                                    {/* Liste du butin — une récompense par ligne */}
                                    <div className="flex flex-col gap-2.5 mb-6">
                                        {revealItems.map((item, index) => {
                                            const isXp = item._xp;
                                            const icon = isXp
                                                ? "/static/icons/xp.svg"
                                                : getRewardIcon(item.type);
                                            const amountText = isXp
                                                ? `+${item.amount}`
                                                : item.type === "xp_booster"
                                                  ? `×${item.metadata?.multiplier ?? 2}`
                                                  : `+${item.amount}`;
                                            const label = isXp
                                                ? t("experience", "Experience")
                                                : translateRewardType(
                                                      item.type,
                                                      item.amount,
                                                      item.metadata,
                                                  );
                                            const descKey = `reward_desc_${isXp ? "xp" : item.type}`;
                                            const desc =
                                                t.has && t.has(descKey)
                                                    ? t(descKey)
                                                    : null;
                                            const hasMultiplier =
                                                !isPremiumEvent &&
                                                ((item.type === "xp_bonus" &&
                                                    multipliers?.xp > 1) ||
                                                    (item.type === "fragment" &&
                                                        multipliers?.fragment >
                                                            1) ||
                                                    (item.type === "shield" &&
                                                        multipliers?.shield >
                                                            1));
                                            const multiplierValue =
                                                item.type === "xp_bonus"
                                                    ? multipliers?.xp
                                                    : item.type === "fragment"
                                                      ? multipliers?.fragment
                                                      : multipliers?.shield;
                                            return (
                                                <motion.div
                                                    key={`loot-${index}`}
                                                    initial={{
                                                        opacity: 0,
                                                        y: 14,
                                                        scale: 0.97,
                                                    }}
                                                    animate={{
                                                        opacity: 1,
                                                        y: 0,
                                                        scale: 1,
                                                    }}
                                                    transition={{
                                                        delay:
                                                            0.08 + index * 0.09,
                                                        type: "spring",
                                                        stiffness: 260,
                                                        damping: 22,
                                                    }}
                                                    className="flex items-center gap-3.5 rounded-2xl p-3 sm:p-3.5 bg-grayDark-800/60 backdrop-blur-md border border-white/10"
                                                >
                                                    <div
                                                        className="w-12 h-12 shrink-0 rounded-full flex items-center justify-center"
                                                        style={{
                                                            background: `radial-gradient(circle at 50% 35%, ${rewardColors.primary}33, ${rewardColors.primary}10)`,
                                                        }}
                                                    >
                                                        <Image
                                                            src={icon}
                                                            width={26}
                                                            height={26}
                                                            alt=""
                                                            unoptimized
                                                        />
                                                    </div>
                                                    <div className="flex-1 min-w-0 text-left">
                                                        <div className="text-white font-semibold text-sm leading-tight">
                                                            {label}
                                                        </div>
                                                        {desc && (
                                                            <div className="text-white/45 text-xs mt-0.5">
                                                                {desc}
                                                            </div>
                                                        )}
                                                    </div>
                                                    {hasMultiplier && (
                                                        <span className="shrink-0 text-[11px] font-bold text-amber-300 bg-amber-400/15 border border-amber-400/30 rounded-full px-2 py-0.5">
                                                            ×{multiplierValue}
                                                        </span>
                                                    )}
                                                    <span
                                                        className="shrink-0 text-lg sm:text-xl font-black text-transparent bg-clip-text"
                                                        style={{
                                                            backgroundImage: `linear-gradient(135deg, ${rewardColors.primary}, ${rewardColors.secondary})`,
                                                        }}
                                                    >
                                                        {amountText}
                                                    </span>
                                                </motion.div>
                                            );
                                        })}

                                        {rewards?.event ===
                                            "double_chest_premium" && (
                                            <motion.div
                                                initial={{ opacity: 0, y: 14 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{
                                                    delay:
                                                        0.08 +
                                                        revealItems.length *
                                                            0.09,
                                                }}
                                                className="flex items-center gap-3.5 rounded-2xl p-3 sm:p-3.5 bg-grayDark-800/30 border border-purple-500/20 opacity-70"
                                            >
                                                <div className="w-12 h-12 shrink-0 rounded-full flex items-center justify-center bg-white/5">
                                                    <span className="text-xl">
                                                        🔒
                                                    </span>
                                                </div>
                                                <div className="flex-1 text-left text-purple-200/80 text-sm font-semibold">
                                                    {t(
                                                        "double_chest_premium_missed",
                                                    )}
                                                </div>
                                            </motion.div>
                                        )}
                                    </div>

                                    {/* Bouton de fermeture / coffre suivant */}
                                    <motion.div
                                        initial={{ y: 16, opacity: 0 }}
                                        animate={{ y: 0, opacity: 1 }}
                                        transition={{ delay: 0.55 }}
                                        className="text-center"
                                    >
                                        <motion.button
                                            onClick={() => {
                                                if (
                                                    isDoubleChest &&
                                                    currentChestIndex === 0
                                                ) {
                                                    goToNextChest();
                                                } else {
                                                    onClose();
                                                }
                                            }}
                                            whileHover={{ scale: 1.04 }}
                                            whileTap={{ scale: 0.96 }}
                                            className="relative group px-8 sm:px-10 py-3 sm:py-4 rounded-xl font-bold text-base sm:text-lg text-white overflow-hidden"
                                            style={{
                                                background: `linear-gradient(135deg, ${rewardColors.primary}, ${rewardColors.secondary})`,
                                                boxShadow: `0 14px 30px ${rewardColors.primary}40`,
                                            }}
                                        >
                                            <div className="absolute inset-0 bg-white/15 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                                            <span className="relative z-10 flex items-center gap-3">
                                                {isDoubleChest &&
                                                currentChestIndex === 0
                                                    ? t(
                                                          "open_next_chest",
                                                          "Ouvrir le coffre suivant",
                                                      )
                                                    : t(
                                                          "collect_continue",
                                                          "Collect & Continue",
                                                      )}
                                                <motion.span
                                                    animate={{ x: [0, 4, 0] }}
                                                    transition={{
                                                        duration: 1.5,
                                                        repeat: Infinity,
                                                        ease: "easeInOut",
                                                    }}
                                                >
                                                    →
                                                </motion.span>
                                            </span>
                                        </motion.button>
                                    </motion.div>
                                </div>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Flash blanc plein écran qui cache la transition coffre → rewards */}
                    <AnimatePresence>
                        {showFlash && (
                            <motion.div
                                key="flash"
                                className="absolute inset-0 z-[100002] pointer-events-none"
                                initial={{ opacity: 0 }}
                                animate={{
                                    opacity:
                                        phase === "flashing" ? [0, 0.95, 0] : 0,
                                }}
                                exit={{ opacity: 0 }}
                                transition={{
                                    duration: phase === "flashing" ? 0.5 : 0.2,
                                    times:
                                        phase === "flashing"
                                            ? [0, 0.3, 1]
                                            : undefined,
                                    ease: "easeOut",
                                }}
                                style={{
                                    background: `radial-gradient(circle at center, white 0%, ${colors.secondary} 60%, transparent 100%)`,
                                }}
                            />
                        )}
                    </AnimatePresence>

                    {/* Indicateurs de progression + texte d'aide en bas */}
                    {showChest && (
                        <div className="fixed bottom-16 left-0 right-0 flex flex-col items-center z-[100001] pointer-events-none">
                            {(tapCount === 0 || tapCount === 5) && (
                                <motion.p
                                    key={tapCount === 0 ? "start" : "ready"}
                                    initial={{ opacity: 0, y: 6 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -6 }}
                                    transition={{ duration: 0.25 }}
                                    className="text-white text-base sm:text-lg font-medium drop-shadow-lg mb-4 mt-0"
                                >
                                    {tapCount === 0
                                        ? t("tap_to_reveal", "Tap to reveal")
                                        : t("tap_to_open", "Tap to open!")}
                                </motion.p>
                            )}
                            <div className="flex gap-3">
                                {Array.from({ length: 5 }).map((_, i) => (
                                    <motion.div
                                        key={i}
                                        className="w-2.5 h-2.5 rounded-full"
                                        initial={{ scale: 0 }}
                                        animate={{
                                            scale: 1,
                                            backgroundColor:
                                                i < tapCount
                                                    ? colors.primary
                                                    : "rgba(255,255,255,0.2)",
                                            boxShadow:
                                                i < tapCount
                                                    ? `0 0 10px ${colors.primary}99`
                                                    : "none",
                                        }}
                                        transition={{
                                            scale: { delay: 0.2 + i * 0.05 },
                                            backgroundColor: { duration: 0.3 },
                                            boxShadow: { duration: 0.3 },
                                        }}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </motion.div>
            )}
        </AnimatePresence>
    );

    return createPortal(modalContent, document.body);
}

// Révélation d'UNE récompense (façon Duolingo) : médaillon qui jaillit, compteur de
// ce qui reste, bouton pour passer à la suivante. Animée en framer-motion (glow lent,
// light-sweep one-shot, étincelles douces) — premium, jouée une fois, jamais en boucle.
function OneByOneReveal({
    index,
    total,
    remaining,
    colors,
    icon,
    amountText,
    label,
    hint,
    isLast,
    onAdvance,
    t,
}) {
    const sparkles = [
        { dx: -54, dy: -36, s: 7, d: 0 },
        { dx: 58, dy: -26, s: 5, d: 0.05 },
        { dx: 42, dy: 46, s: 6, d: 0.1 },
        { dx: -46, dy: 42, s: 5, d: 0.14 },
        { dx: 4, dy: -62, s: 4, d: 0.08 },
    ];
    return (
        <div className="relative w-full max-w-sm mx-4 px-4 flex flex-col items-center text-center">
            {/* Compteur : points de progression + récompenses restantes */}
            <div className="flex items-center gap-2 mb-2.5">
                {Array.from({ length: total }).map((_, i) => (
                    <div
                        key={i}
                        className="h-2 rounded-full transition-all duration-300"
                        style={{
                            width: i === index ? 22 : 8,
                            backgroundColor:
                                i <= index
                                    ? colors.primary
                                    : "rgba(255,255,255,0.18)",
                            boxShadow:
                                i <= index
                                    ? `0 0 8px ${colors.primary}99`
                                    : "none",
                        }}
                    />
                ))}
            </div>
            <p className="text-white/55 text-xs font-medium mb-10 mt-0">
                {remaining > 1
                    ? t("chest_reveal_remaining", { count: remaining })
                    : remaining === 1
                      ? t("chest_reveal_remaining_one")
                      : t("chest_reveal_last")}
            </p>

            {/* Stage à hauteur fixe : l'ancienne récompense glisse vers le haut
                pendant que la suivante monte depuis le bas (convoyeur, sans temps mort). */}
            <div className="relative w-full h-[264px]">
                <AnimatePresence>
                    <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 64 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -64 }}
                        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                        className="absolute inset-0 flex flex-col items-center justify-center"
                    >
                        {/* Médaillon récompense */}
                        <motion.div
                            initial={{ scale: 0.62 }}
                            animate={{ scale: 1 }}
                            transition={{
                                type: "spring",
                                stiffness: 240,
                                damping: 16,
                                delay: 0.06,
                            }}
                            className="relative w-32 h-32 rounded-full flex items-center justify-center mb-7"
                            style={{
                                background: `radial-gradient(circle at 50% 38%, ${colors.primary}3a, ${colors.primary}12)`,
                                boxShadow: `0 0 50px ${colors.primary}40, inset 0 1px 0 rgba(255,255,255,0.12)`,
                            }}
                        >
                            {/* Glow pulse lent (halo vivant, pas clignotant) */}
                            <motion.div
                                className="absolute inset-0 rounded-full"
                                animate={{ opacity: [0.3, 0.55, 0.4] }}
                                transition={{
                                    duration: 2.6,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                }}
                                style={{
                                    background: `radial-gradient(circle, ${colors.primary}55, transparent 70%)`,
                                    filter: "blur(16px)",
                                }}
                            />
                            {/* Light-sweep diagonal, une seule passe */}
                            <div className="absolute inset-0 rounded-full overflow-hidden">
                                <motion.div
                                    className="absolute top-0 left-0 h-full w-1/2 -skew-x-12 bg-gradient-to-r from-transparent via-white/25 to-transparent"
                                    initial={{ x: -90 }}
                                    animate={{ x: 210 }}
                                    transition={{
                                        duration: 0.7,
                                        delay: 0.3,
                                        ease: "easeOut",
                                    }}
                                />
                            </div>
                            {/* Étincelles douces (monochrome rareté), une fois */}
                            {sparkles.map((sp, i) => (
                                <motion.span
                                    key={i}
                                    className="absolute top-1/2 left-1/2 rounded-full"
                                    style={{
                                        width: sp.s,
                                        height: sp.s,
                                        backgroundColor: colors.secondary,
                                    }}
                                    initial={{ opacity: 0, x: 0, y: 0, scale: 0 }}
                                    animate={{
                                        opacity: [0, 1, 0],
                                        x: sp.dx,
                                        y: sp.dy,
                                        scale: [0, 1, 0.6],
                                    }}
                                    transition={{
                                        duration: 0.8,
                                        delay: 0.28 + sp.d,
                                        ease: "easeOut",
                                    }}
                                />
                            ))}
                            <Image
                                src={icon}
                                width={56}
                                height={56}
                                alt=""
                                className="relative z-10"
                                unoptimized
                            />
                        </motion.div>

                        {/* Montant + libellé */}
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.18, duration: 0.4 }}
                            className="flex flex-col items-center"
                        >
                            <span
                                className="text-4xl sm:text-5xl font-black text-transparent bg-clip-text leading-none mb-2"
                                style={{
                                    backgroundImage: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                                }}
                            >
                                {amountText}
                            </span>
                            <span className="text-white text-base font-semibold">
                                {label}
                            </span>
                            {hint && (
                                <span className="mt-3 max-w-[280px] text-center text-sm leading-relaxed text-white/90">
                                    {hint}
                                </span>
                            )}
                        </motion.div>
                    </motion.div>
                </AnimatePresence>
            </div>

            {/* Bouton : récompense suivante ou voir le butin */}
            <motion.button
                onClick={onAdvance}
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
                className="relative group mt-12 px-9 sm:px-10 py-3.5 rounded-xl font-bold text-base sm:text-lg text-white overflow-hidden"
                style={{
                    background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                    boxShadow: `0 14px 30px ${colors.primary}40`,
                }}
            >
                <div className="absolute inset-0 bg-white/15 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                <span className="relative z-10">
                    {isLast
                        ? t("chest_reveal_see_loot")
                        : t("chest_reveal_next")}
                </span>
            </motion.button>
        </div>
    );
}
